// /w/:wid/guild — the guild hall. In a guild: the hero (crest, name, tag, rank, motto; members / points / average /
// best), GUILD (members · requests · invites) / DIPLOMACY (designate by tag, allies / pacts / enemies) / MANAGE
// (greeting · announce · leave · disband). Not in one: the NoGuild screen (find, invitations, found a guild).
// Every action answers with the refreshed city; permissions follow the server's level thresholds (src/game/guild.ts).
import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import type { GuildDetails, GuildMember } from "@/api/types";
import { api } from "@/api/client";
import { WorldPage, useWorldCity } from "@/app/WorldPage";
import { art } from "@/kit/art";
import { Collapsible } from "@/kit/city";
import { Flourish, IconChevronDown, IconCrown, IconInfo, IconMail, IconShield, IconSwords } from "@/kit/icons";
import { Confirm, Sheet } from "@/kit/overlays";
import { Button, Card, CardHead, Disc, Empty, Medallion, Row, RowText, Seg, Skeleton, Tabs, cx } from "@/kit/primitives";
import { useSession } from "@/state/session";
import { usePrompts } from "@/state/prompts";
import { useGuildMembersRanking } from "@/state/socialQueries";
import { usePlayerInfo } from "@/state/mapQueries";
import { useWorldsOverview, worldDetails } from "@/state/queries";
import { useRememberedOpenSet } from "@/state/collapse";
import { fmtNum, fmtRank, plural } from "@/game/format";
import { DIPLOMACY_KINDS, can, guildLimits, guildOf, guildStats, idLogins, levelName, memberLimit, myLevel, sortMembers, type DiplomacyType } from "@/game/guild";
import { NoGuild } from "./NoGuild";
import { GuildPeek } from "./GuildPeek";
import "../social.css";
import "./guild.css";

type Tab = "guild" | "diplomacy" | "manage";
type Sub = "members" | "requests" | "invites" | "greeting" | "announce" | "leave" | "disband";

export function GuildScreen() {
  return (
    <WorldPage citySwipe layout="col">
      <Guild />
    </WorldPage>
  );
}

function Guild() {
  const { v, loading, error, wid } = useWorldCity();
  const g = guildOf(v);
  if (!v) {
    return loading ? <div className="stack"><Skeleton h={300} /><Skeleton h={200} /></div> : error ? <Card><Empty>Couldn't read your city.</Empty></Card> : null;
  }
  if (!g) return <NoGuild key={`none-${wid}`} />;
  return <GuildHall key={g.id} g={g} />;
}

// ------------------------------------------------------------------ the hall

function GuildHall({ g }: { g: GuildDetails }) {
  const { v, wid, vid } = useWorldCity();
  const session = useSession();
  const [params, setParams] = useSearchParams();
  const tab = (["guild", "diplomacy", "manage"].includes(params.get("tab") ?? "") ? params.get("tab") : "guild") as Tab;
  const subP = params.get("sub") as Sub | null;
  const level = myLevel(v, session.uid);
  const sub: Sub = tab === "guild" ? (subP && ["members", "requests", "invites"].includes(subP) ? subP : "members") : tab === "manage" ? (subP && ["greeting", "announce", "leave", ...(can.disband(level) ? ["disband"] : [])].includes(subP) ? subP : "greeting") : "members";
  const go = (t: Tab, s?: Sub) => setParams(Object.fromEntries(Object.entries({ tab: t === "guild" && (!s || s === "members") ? "" : t, sub: s && s !== "members" && s !== "greeting" ? s : "" }).filter(([, val]) => val)), { replace: true });

  const ranking = useGuildMembersRanking(wid);
  const points = useMemo(() => {
    const m = new Map<number, { points: number; cities: number; rank: number }>();
    for (const r of ranking.data?.guild_info?.members ?? []) m.set(r.user_id, { points: r.total_points, cities: r.num_villages, rank: r.rank });
    return m;
  }, [ranking.data]);
  const stats = guildStats(g, points);
  const requests = idLogins(g.request_list);
  const invites = idLogins(g.invite_list);
  const worlds = useWorldsOverview(wid);
  const limit = memberLimit(worldDetails(worlds.data, wid)?.["Guild Member Limit"]);
  const [peek, setPeek] = useState<{ gid: number; tag?: string } | null>(null);
  const panels = useRememberedOpenSet("guild");
  const open = { news: panels.is("news"), welcome: panels.is("welcome") };

  const header = (
    <Card bare plain className="hero c-hero gd-hero">
      {can.edit(level) && <Disc size="sm" label="Manage the guild" className="c-hero-gear" onClick={() => go("manage")}><IconShield /></Disc>}
      <div className="body">
        <div className="gd-herorow">
          <img src={art.social.crest} alt="" className="gd-crest" decoding="async" />
          <div className="gd-herotxt">
            <h2>{g.name}</h2>
            {g.description && <p className="gd-motto">“{g.description}”</p>}
          </div>
        </div>
      </div>
      <div className="gd-stats">
        <dl className="gd-statgroup">
          <div><dt>Tag</dt><dd className="gd-tag">[{g.tag}]</dd></div>
          <div><dt>Members</dt><dd>{fmtNum(stats.members)}{limit ? <small> / {limit}</small> : null}</dd></div>
          <div><dt>Rank</dt><dd>{stats.rank ? fmtRank(stats.rank) : "—"}</dd></div>
        </dl>
        <dl className="gd-statgroup">
          <div><dt>Total points</dt><dd>{fmtNum(stats.total)}</dd></div>
          <div><dt>Average</dt><dd>{fmtNum(stats.average)}</dd></div>
          <div><dt>Best player</dt><dd>{fmtNum(stats.best)}</dd></div>
        </dl>
      </div>
    </Card>
  );

  // Rev. 11 §6 order: header/resources → Guild · Diplomacy · Manage → emblem, identity and stats → the selected tab's
  // subtabs (Members · Requests · Invites, or Manage's) → its content. Each strip shows once.
  return (
    <div className="stack gd-hall">
      <Seg value={tab} onChange={(t) => go(t)} options={[{ value: "guild", label: "Guild", badge: can.manageRequests(level) ? requests.length || undefined : undefined }, { value: "diplomacy", label: "Diplomacy" }, { value: "manage", label: "Manage" }]} />
      {header}

      {tab === "guild" && (
        <>
          <Tabs value={sub} onChange={(s) => go("guild", s)} options={[{ value: "members", label: "Members" }, { value: "requests", label: <>Requests{requests.length ? <span className="badge inline">{requests.length}</span> : null}</> }, { value: "invites", label: "Invites" }]} />
          {sub === "members" && <Members g={g} level={level} points={points} />}
          {sub === "requests" && <Requests g={g} level={level} limit={limit} />}
          {sub === "invites" && <Invites g={g} level={level} invites={invites} />}
          {sub === "members" && (
            <>
              <Collapsible title="Announcement" aside={g.news ? undefined : <>None posted{can.edit(level) ? <> · set it in <b className="gold">Manage</b></> : null}</>} open={!!g.news && open.news} onToggle={() => (g.news ? panels.toggle("news") : can.edit(level) ? go("manage", "announce") : undefined)}>
                <p className="ml-body" style={{ margin: 0 }}>{g.news}</p>
              </Collapsible>
              <Collapsible title="Welcome message" aside={g.welcome_mail?.body ? `“${g.welcome_mail.body.slice(0, 32)}${g.welcome_mail.body.length > 32 ? "…" : ""}”` : "None set"} open={open.welcome} onToggle={() => (g.welcome_mail?.body ? panels.toggle("welcome") : can.edit(level) ? go("manage", "greeting") : undefined)}>
                {g.welcome_mail?.subject && <p className="eyebrow" style={{ margin: "0 0 8px" }}>{g.welcome_mail.subject}</p>}
                <p className="ml-body" style={{ margin: 0 }}>{g.welcome_mail?.body}</p>
              </Collapsible>
            </>
          )}
        </>
      )}
      {tab === "diplomacy" && <Diplomacy g={g} level={level} onPeek={(gid, tag) => setPeek({ gid, tag })} />}
      {tab === "manage" && (
        <>
          <Tabs value={sub} onChange={(s) => go("manage", s)} options={[{ value: "greeting", label: "Greeting" }, { value: "announce", label: "Announce" }, { value: "leave", label: "Leave" }, ...(can.disband(level) ? [{ value: "disband" as Sub, label: "Disband" }] : [])]} />
          {sub === "greeting" && <Greeting g={g} level={level} />}
          {sub === "announce" && <Announce g={g} level={level} />}
          {sub === "leave" && <Leave g={g} level={level} />}
          {sub === "disband" && can.disband(level) && <Disband g={g} />}
        </>
      )}

      <GuildPeek wid={wid} vid={vid} target={peek} onClose={() => setPeek(null)} myGid={g.id} level={level} />
    </div>
  );
}

// ------------------------------------------------------------------ members

function Members({ g, level, points }: { g: GuildDetails; level: number; points: Map<number, { points: number; cities: number; rank: number }> }) {
  const { wid, vid, actions, base } = useWorldCity();
  const nav = useNavigate();
  const session = useSession();
  const [edit, setEdit] = useState<GuildMember | null>(null);
  const [kick, setKick] = useState<GuildMember | null>(null);
  const members = sortMembers(g.members, points);
  const icon = (lvl: number) => (lvl === 0 ? <IconCrown /> : lvl === 1 ? <IconShield /> : lvl === 2 ? <IconSwords /> : null);
  const run = (label: string, fn: () => Promise<unknown>, success: string) => actions.run(label, fn, { success });
  return (
    <>
      <Card className="gd-members">
        <CardHead title="Members" aside={`${plural(members.length, "member")} · by points`} />
        <div className="gd-colhead"><span>Name</span><span>Points</span><span>Cities</span><span className="sr-only">Actions</span></div>
        <div className="rows">
          {members.map((m) => {
            const me = m.user_id === session.uid;
            const p = points.get(m.user_id);
            const canEdit = !me && (can.setLevel(level) || can.kick(level, Number(m.level)));
            return (
              <Row key={m.user_id} className={cx("gd-member", me && "unread")}>
                <RowText
                  line={<span className="gd-name"><span className="gd-role" aria-hidden="true">{icon(Number(m.level))}</span><span className="nm">{m.login}</span></span>}
                  meta={`${levelName(m.level)}${me ? " · you" : ""}${p?.rank ? ` · #${p.rank} in the guild` : ""}`}
                />
                <div className="gd-cols">
                  <span className="col pts">{p ? fmtNum(p.points) : "—"}</span>
                  <span className="col cities">{fmtNum(p?.cities ?? m.num_villages)}</span>
                </div>
                <div className="gd-acts">
                  <Disc size="sm" label={`${m.login}'s profile`} onClick={() => nav(`${base}/player/${m.user_id}`)}><IconInfo /></Disc>
                  <Button size="sm" kind="steel" disabled={!canEdit} onClick={() => setEdit(m)}>Edit</Button>
                </div>
              </Row>
            );
          })}
        </div>
      </Card>

      <Sheet open={!!edit} onClose={() => setEdit(null)} label={edit ? `Edit ${edit.login}` : "Edit member"} title={edit ? <h3 className="eyebrow" style={{ margin: "0 0 14px" }}>{edit.login} · {levelName(edit.level)}</h3> : undefined}>
        {edit && (
          <div className="stack" style={{ gap: 12 }}>
            {can.setLevel(level) ? (
              <>
                <p className="c-note left" style={{ margin: 0 }}>Rank in the guild — Warlords run everything, Generals manage members and diplomacy, Captains and Warriors fight.</p>
                <Seg value={String(edit.level)} onChange={(lv) => { const next = Number(lv); if (next === Number(edit.level)) return; void run(`level-${edit.user_id}`, () => api.guild.setLevel({ wid, vid: vid! }, edit.user_id, next), `${edit.login} is now a ${levelName(next)}.`).then(() => setEdit(null)); }} options={[{ value: "0", label: "Warlord" }, { value: "1", label: "General" }, { value: "2", label: "Captain" }, { value: "3", label: "Warrior" }]} />
              </>
            ) : (
              <p className="c-note left" style={{ margin: 0 }}>Only the Warlord changes ranks.</p>
            )}
            <Button kind="steel" size="md" wide onClick={() => { setEdit(null); nav(`${base}/mail/new?to=${encodeURIComponent(edit.login)}`); }}><IconMail /> Write to {edit.login}</Button>
            {can.kick(level, Number(edit.level)) && <Button kind="crimson" size="md" wide onClick={() => { setKick(edit); setEdit(null); }}>Kick from the guild</Button>}
          </div>
        )}
      </Sheet>
      <Confirm open={!!kick} onClose={() => setKick(null)} title={`Kick ${kick?.login}?`} text={`${kick?.login} leaves the guild at once and loses its diplomacy on the map. They can request to come back.`} action="Kick" busy={actions.isBusy("kick")} onConfirm={() => { const k = kick!; void run("kick", () => api.guild.kick({ wid, vid: vid! }, k.user_id), `${k.login} was kicked.`).then(() => setKick(null)); }} />
    </>
  );
}

// ------------------------------------------------------------------ requests

function Requests({ g, level, limit }: { g: GuildDetails; level: number; limit: number | null }) {
  const requests = idLogins(g.request_list);
  const open = limit ? Math.max(0, limit - g.members.length) : null;
  return (
    <Card>
      <CardHead title="Requests" badge={requests.length || undefined} aside={open !== null ? `${open} of ${limit} seats open` : plural(requests.length, "request")} />
      {!requests.length && <Empty>Nobody is asking to join right now. Players request from the guild list on their side.</Empty>}
      <div className="rows">
        {requests.map((r) => <RequestRow key={r.user_id} uid={r.user_id} login={r.login} canAct={can.manageRequests(level)} full={open === 0} />)}
      </div>
      {!can.manageRequests(level) && requests.length > 0 && <p className="c-note">Warlords and Generals accept or reject requests.</p>}
    </Card>
  );
}

function RequestRow({ uid, login, canAct, full }: { uid: number; login: string; canAct: boolean; full: boolean }) {
  const { wid, vid, actions, base } = useWorldCity();
  const nav = useNavigate();
  const info = usePlayerInfo(wid, uid);
  const tc = Object.values(info.data?.player_details ?? {})[0]?.target_char;
  const pts = info.data?.player_stats?.total_points;
  const meta = tc ? `${plural(tc.villages?.length ?? 0, "city", "cities")}${pts ? ` · ${fmtNum(pts)} pts` : ""}` : info.isLoading ? "…" : "";
  const run = (label: string, fn: () => Promise<unknown>, success: string) => actions.run(label, fn, { success });
  return (
    <Row className="gd-req">
      <RowText line={<span className="gd-name"><span className="nm">{login}</span></span>} meta={meta || undefined} />
      <div className="gd-acts">
        <Disc size="sm" label={`${login}'s profile`} onClick={() => nav(`${base}/player/${uid}`)}><IconInfo /></Disc>
        {canAct && <Button size="sm" kind="gold" disabled={full} busy={actions.isBusy(`accept-${uid}`)} onClick={() => void run(`accept-${uid}`, () => api.guild.acceptRequest({ wid, vid: vid! }, uid, login), `${login} joined the guild.`)}>Accept</Button>}
        {canAct && <Button size="sm" kind="steel" busy={actions.isBusy(`decline-${uid}`)} onClick={() => void run(`decline-${uid}`, () => api.guild.declineRequest({ wid, vid: vid! }, uid, login), `${login}'s request was declined.`)}>Reject</Button>}
      </div>
    </Row>
  );
}

// ------------------------------------------------------------------ invites

function Invites({ g, level, invites }: { g: GuildDetails; level: number; invites: { user_id: number; login: string }[] }) {
  const { v, wid, vid, actions } = useWorldCity();
  const [name, setName] = useState("");
  const limits = guildLimits(v);
  const run = (label: string, fn: () => Promise<unknown>, success: string) => actions.run(label, fn, { success });
  const invite = async () => {
    const n = name.trim();
    if (!n) return;
    const r = await run("invite", () => api.guild.addInvite({ wid, vid: vid! }, g.id, n), `${n} was invited.`);
    if (r) setName("");
  };
  return (
    <Card>
      <CardHead title="Invites" aside={limits.sendInvites ? `${invites.length} of ${limits.sendInvites} out` : plural(invites.length, "invite")} />
      {can.manageInvites(level) && (
        <form className="field-row" style={{ marginBottom: 14 }} onSubmit={(e) => { e.preventDefault(); void invite(); }}>
          <input className="input" value={name} placeholder="Player name — exact spelling" aria-label="Player to invite" autoComplete="off" autoCapitalize="none" onChange={(e) => setName(e.target.value)} />
          <Button type="submit" kind="crimson" size="md" disabled={!name.trim() || (limits.sendInvites !== null && invites.length >= limits.sendInvites)} busy={actions.isBusy("invite")}>Invite</Button>
        </form>
      )}
      {!invites.length && <Empty>{can.manageInvites(level) ? "No invitations are out. Invite a player by name above." : "No invitations are out."}</Empty>}
      <div className="rows">
        {invites.map((i) => (
          <Row key={i.user_id}>
            <Medallion initial={i.login} size={54} />
            <RowText line={<span className="gd-name"><span className="nm">{i.login}</span></span>} meta="Invited · waiting on their answer" />
            {can.manageInvites(level) && <div className="gd-acts"><Button size="sm" kind="steel" busy={actions.isBusy(`uninvite-${i.user_id}`)} onClick={() => void run(`uninvite-${i.user_id}`, () => api.guild.removeInvite({ wid, vid: vid! }, i.user_id), `The invitation to ${i.login} was withdrawn.`)}>Cancel</Button></div>}
          </Row>
        ))}
      </div>
    </Card>
  );
}

// ------------------------------------------------------------------ diplomacy

function Diplomacy({ g, level, onPeek }: { g: GuildDetails; level: number; onPeek: (gid: number, tag?: string) => void }) {
  const { wid, vid, actions } = useWorldCity();
  const [tag, setTag] = useState("");
  const [type, setType] = useState<DiplomacyType>("a");
  const kinds = useRememberedOpenSet("guild.diplomacy");
  const [rescind, setRescind] = useState<{ tag: string; name: string } | null>(null);
  const kind = DIPLOMACY_KINDS.find((k) => k.type === type)!;
  const lists: Record<DiplomacyType, { gid: number; gtag: string; gname: string }[]> = { a: g.diplomacy?.allies ?? [], n: g.diplomacy?.nap ?? [], e: g.diplomacy?.enemies ?? [] };
  const run = (label: string, fn: () => Promise<unknown>, success: string) => actions.run(label, fn, { success });
  const designate = async () => {
    const t = tag.trim();
    if (!t) return;
    if (t.toLowerCase() === g.tag.toLowerCase()) return usePrompts.getState().toast("That's your own guild.", "error");
    const r = await run("designate", () => api.guild.addDiplomacy({ wid, vid: vid! }, g.id, t, type), `[${t.toUpperCase()}] marked as ${kind.one.toLowerCase()}.`);
    if (r) setTag("");
  };
  return (
    <>
      {can.diplomacy(level) ? (
        <Card>
          <CardHead title="Designate by guild tag" />
          <form className="gd-designate" onSubmit={(e) => { e.preventDefault(); void designate(); }}>
            <input className="input" value={tag} placeholder="Guild tag" aria-label="Guild tag" autoComplete="off" autoCapitalize="characters" maxLength={6} onChange={(e) => setTag(e.target.value)} />
            <label className="gd-select" style={{ ["--dot" as string]: kind.color }}>
              <span className="sr-only">Relationship</span>
              <select value={type} onChange={(e) => setType(e.target.value as DiplomacyType)}>
                {DIPLOMACY_KINDS.map((k) => <option key={k.type} value={k.type}>{k.label}</option>)}
              </select>
              <IconChevronDown />
            </label>
            <Button type="submit" kind="crimson" size="md" disabled={!tag.trim()} busy={actions.isBusy("designate")}>Designate</Button>
          </form>
        </Card>
      ) : (
        <p className="c-note">Warlords and Generals set the guild's diplomacy. The map colours cities by it.</p>
      )}
      {DIPLOMACY_KINDS.map((k) => {
        const list = lists[k.type];
        return (
          <Card key={k.type} className="gd-dip">
            <CardHead title={k.label} aside={<>{list.length}</>} onToggle={() => kinds.toggle(k.type)} open={kinds.is(k.type)} />
            {kinds.is(k.type) && (
              <>
                {!list.length && <div className="gd-dipline" style={{ ["--dot" as string]: k.color }}><i />{k.quip}</div>}
                <div className="rows">
                  {list.map((d) => (
                    <Row key={d.gid} className={`edge-${k.type}`}>
                      <Medallion src={art.social.crest} alt="" size={60} />
                      <RowText line={<span className="gd-name"><span className="nm">{d.gname}</span></span>} meta={<><b className="gold">[{d.gtag}]</b> · {k.one.toLowerCase()}</>} />
                      <div className="gd-acts">
                        <Disc size="sm" label={`About ${d.gname}`} onClick={() => onPeek(d.gid, d.gtag)}><IconInfo /></Disc>
                        {can.diplomacy(level) && <Button size="sm" kind="steel" onClick={() => setRescind({ tag: d.gtag, name: d.gname })}>Rescind</Button>}
                      </div>
                    </Row>
                  ))}
                </div>
              </>
            )}
          </Card>
        );
      })}
      <Confirm open={!!rescind} onClose={() => setRescind(null)} title={`Rescind with ${rescind?.name}?`} text={`[${rescind?.tag}] goes back to unaligned on the map for your whole guild.`} action="Rescind" kind="steel" busy={actions.isBusy("rescind")} onConfirm={() => { const r = rescind!; void run("rescind", () => api.guild.removeDiplomacy({ wid, vid: vid! }, g.id, r.tag), `[${r.tag}] is unaligned again.`).then(() => setRescind(null)); }} />
    </>
  );
}

// ------------------------------------------------------------------ manage

const GREETING_MAX = 500;
const NEWS_MAX = 1000;

function Greeting({ g, level }: { g: GuildDetails; level: number }) {
  const { wid, vid, actions } = useWorldCity();
  const [subject, setSubject] = useState(g.welcome_mail?.subject ?? "");
  const [body, setBody] = useState(g.welcome_mail?.body ?? "");
  const dirty = subject !== (g.welcome_mail?.subject ?? "") || body !== (g.welcome_mail?.body ?? "");
  if (!can.edit(level)) return <Card><Empty>Warlords and Generals write the welcome message.</Empty></Card>;
  return (
    <Card>
      <CardHead title="Welcome message" />
      <p className="gd-tagline">Mailed to every new member the moment they join.</p>
      <div className="gd-form">
        <div className="field"><label htmlFor="gd-subj">Subject</label><input id="gd-subj" className="input" value={subject} maxLength={80} onChange={(e) => setSubject(e.target.value)} placeholder={`Welcome to ${g.name}`} /></div>
        <div className="field"><label htmlFor="gd-body">Message</label><textarea id="gd-body" className="input" value={body} maxLength={GREETING_MAX} onChange={(e) => setBody(e.target.value)} placeholder="One of us! Stronger together!" /></div>
        <div className={cx("counter", body.length >= GREETING_MAX && "over")}>{body.length} / {GREETING_MAX}</div>
        <Button kind="crimson" wide disabled={!dirty || !subject.trim() || !body.trim()} busy={actions.isBusy("welcome")} onClick={() => void actions.run("welcome", () => api.guild.editWelcomeMail({ wid, vid: vid! }, g.id, subject.trim(), body.trim()), { success: "Welcome message saved." })}>Save welcome message</Button>
      </div>
    </Card>
  );
}

function Announce({ g, level }: { g: GuildDetails; level: number }) {
  const { wid, vid, actions } = useWorldCity();
  const [news, setNews] = useState(g.news ?? "");
  const dirty = news !== (g.news ?? "");
  if (!can.edit(level)) return <Card><Empty>Warlords and Generals post announcements.</Empty></Card>;
  return (
    <Card>
      <CardHead title="Announcement" />
      <p className="gd-tagline">Shown to every member at the top of the guild hall.</p>
      <div className="gd-form">
        <textarea className="input" aria-label="Announcement" value={news} maxLength={NEWS_MAX} onChange={(e) => setNews(e.target.value)} placeholder="Rally at Ironfell on Sunday. Stack Guardians in the border cities." />
        <div className={cx("counter", news.length >= NEWS_MAX && "over")}>{news.length} / {NEWS_MAX}</div>
        <div className="pf-actions">
          <Button kind="steel" disabled={!g.news || actions.isBusy("news")} onClick={() => { setNews(""); void actions.run("news", () => api.guild.editNews({ wid, vid: vid! }, g.id, ""), { success: "Announcement cleared." }); }}>Clear</Button>
          <Button kind="crimson" disabled={!dirty} busy={actions.isBusy("news")} onClick={() => void actions.run("news", () => api.guild.editNews({ wid, vid: vid! }, g.id, news.trim()), { success: "Announcement posted." })}>Post announcement</Button>
        </div>
      </div>
    </Card>
  );
}

function Leave({ g, level }: { g: GuildDetails; level: number }) {
  const { wid, vid, actions } = useWorldCity();
  const [typed, setTyped] = useState("");
  const ok = typed.trim().toLowerCase() === g.tag.toLowerCase();
  const alone = g.members.length <= 1;
  const otherWarlord = g.members.some((m) => Number(m.level) === 0 && m.user_id !== useSession.getState().uid);
  return (
    <Card>
      <div className="gd-grave">
        <h3><Flourish /> Leaving the hall <Flourish mirror /></h3>
        <p>Your cities lose the guild's colours on the map and its allies' protection. You can ask to come back, but a Warlord or General has to let you in.</p>
        {level === 0 && !alone && !otherWarlord && <p className="crimtxt">You are the only Warlord. Promote someone first, or the guild is left without one.</p>}
        {alone && <p>You are the last member — leaving dissolves the guild.</p>}
        <div className="lbl">Type the guild tag to confirm</div>
        <input className="input" value={typed} placeholder={g.tag} aria-label="Guild tag" autoComplete="off" autoCapitalize="characters" onChange={(e) => setTyped(e.target.value)} />
        <Button kind="crimson" disabled={!ok} busy={actions.isBusy("leave")} onClick={() => void actions.run("leave", () => api.guild.leave({ wid, vid: vid! }), { success: `You left ${g.name}.` })}>Leave this guild</Button>
      </div>
    </Card>
  );
}

function Disband({ g }: { g: GuildDetails }) {
  const { wid, vid, actions } = useWorldCity();
  const [typed, setTyped] = useState("");
  const ok = typed.trim().toLowerCase() === g.tag.toLowerCase();
  return (
    <Card>
      <div className="gd-grave">
        <h3><Flourish /> All things must come to an end <Flourish mirror /></h3>
        <p>So must it be with this brotherhood of fighters.</p>
        <p>Few are brave enough to see the end in the eye and walk unflinchingly towards it. Fewer still are those with enough resolve to not have second thoughts after the fact. And yet, some are foolish enough to push a big red button, just to see what it does.</p>
        <p>Which will you be?</p>
        <div className="lbl">Type the guild tag to confirm</div>
        <input className="input" value={typed} placeholder={g.tag} aria-label="Guild tag" autoComplete="off" autoCapitalize="characters" onChange={(e) => setTyped(e.target.value)} />
        <Button kind="crimson" disabled={!ok} busy={actions.isBusy("destroy")} onClick={() => void actions.run("destroy", () => api.guild.destroy({ wid, vid: vid! }, g.id), { success: `${g.name} is no more.` })}>Destroy this guild forever</Button>
      </div>
    </Card>
  );
}

export type { Tab as GuildTab, Sub as GuildSub };
